import React, { useState } from "react";
import {
  Box,
  Card,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
  Divider,
  Button,
  alpha,
} from "@mui/material";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import DescriptionIcon from "@mui/icons-material/Description";
import s3Utils from "../../../utils/s3Utils";

const border = "1px solid #dcdcdc";
const label = { fontWeight: 600, backgroundColor: "#f8f9fa", padding: "8px" };
const sectionHeader = {
  backgroundColor: "#2c3e50",
  color: "white",
  fontWeight: 700,
  textAlign: "center",
  padding: "12px",
};

const TestingWitnessedForm11Calibration = ({ data, laboratoryName, assessmentDate, facility }) => {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImage, setLightboxImage] = useState("");

  const handleOpenLightbox = (imageUrl) => {
    setLightboxImage(imageUrl);
    setLightboxOpen(true);
  };

  const handleCloseLightbox = () => {
    setLightboxOpen(false);
    setLightboxImage("");
  };

  if (!data || data.length === 0) return null;

  const colors = {
    primary: { main: "#1976d2" },
    error: { main: "#d32f2f" },
    success: { main: "#2e7d32" },
    grey: { 100: "#f5f5f5", 300: "#e0e0e0" },
  };

  // Group all entries by assessor (no distinction between testing/retesting)
  const groupTestsByAssessor = (tests) => {
    return tests.reduce((acc, test) => {
      const assessorName = test.assessorName || "Unknown Assessor";
      if (!acc[assessorName]) {
        acc[assessorName] = [];
      }
      acc[assessorName].push(test);
      return acc;
    }, {});
  };

  const groupedData = groupTestsByAssessor(data);

  const renderSection = () => {
    if (!groupedData || Object.keys(groupedData).length === 0) return null;

    return (
      <Card elevation={3} sx={{ mb: 4 }}>
        {/* Section Header */}
        <Box sx={sectionHeader}>
          <Typography variant="h5" fontWeight={700}>
            Calibration Witness
          </Typography>
        </Box>

        <TableContainer component={Paper}>
          <Table size="small">
            <TableBody>
              {Object.entries(groupedData).map(([assessorName, assessorTests], assessorIndex) => (
                <React.Fragment key={assessorIndex}>
                  {/* Assessor Header */}
                  <TableRow>
                    <TableCell
                      colSpan={2}
                      sx={{
                        border,
                        backgroundColor: "#1976d2",
                        color: "#fff",
                        fontWeight: 700,
                        fontSize: "1rem",
                        py: 2,
                        textAlign: "center"
                      }}
                    >
                      Assessor: {assessorName}
                    </TableCell>
                  </TableRow>

                  {assessorTests.map((test, testIndex) => (
                    <React.Fragment key={testIndex}>
                      {/* Test Number Header */}
                      <TableRow>
                        <TableCell
                          colSpan={2}
                          sx={{
                            border,
                            backgroundColor: "#34495e",
                            color: "#fff",
                            fontWeight: 700,
                            textAlign: "center",
                            py: 1
                          }}
                        >
                          Test {testIndex + 1}
                        </TableCell>
                      </TableRow>

                      {/* Calibration Points Taken */}
                      <TableRow>
                        <TableCell sx={{ ...label, border, width: "40%" }}>
                          Calibration Points Taken
                        </TableCell>
                        <TableCell sx={{ border }}>
                          {test.freshSampleId || "N/A"}
                        </TableCell>
                      </TableRow>

                      {/* CMC */}
                      <TableRow>
                        <TableCell sx={{ ...label, border }}>
                          CMC
                        </TableCell>
                        <TableCell sx={{ border }}>
                          {test.specifiedValue || "N/A"}
                        </TableCell>
                      </TableRow>

                      {/* Remark */}
                      <TableRow>
                        <TableCell sx={{ ...label, border }}>
                          Remark
                        </TableCell>
                        <TableCell sx={{ border }}>
                          {test.observation || "N/A"}
                        </TableCell>
                      </TableRow>

                      {/* Conclusion */}
                      <TableRow>
                        <TableCell sx={{ ...label, border }}>
                          Conclusion on the technical competence of the lab for the test witnessed
                        </TableCell>
                        <TableCell sx={{ border }}>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 600,
                              color: test.conclusion === "competent" ? "success.main" : "error.main",
                              textTransform: "capitalize",
                            }}
                          >
                            {test.conclusion || "N/A"}
                          </Typography>
                        </TableCell>
                      </TableRow>

                      {/* Evidence Files */}
                      {test.evidence && test.evidence.length > 0 && (
                        <>
                          <TableRow>
                            <TableCell
                              colSpan={2}
                              sx={{
                                backgroundColor: "#3498db",
                                color: "white",
                                fontWeight: 700,
                                padding: "8px",
                                border,
                                textAlign: "center"
                              }}
                            >
                              Evidence Files ({test.evidence.length})
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell colSpan={2} sx={border}>
                              <Box
                                sx={{
                                  display: "flex",
                                  flexWrap: "wrap",
                                  gap: 2,
                                  p: 1,
                                }}
                              >
                                {test.evidence.map((img, imgIndex) => {
                                  const docLower = img.imgfile.toLowerCase();
                                  const isImage = docLower.match(/\.(jpg|jpeg|png|gif|webp)$/);
                                  const isPdf = docLower.endsWith(".pdf");
                                  const isExcel = docLower.match(/\.(xlsx|xls)$/);

                                  return (
                                    <WorkingSpaceDocument
                                      key={imgIndex}
                                      doc={img.imgfile}
                                      index={imgIndex}
                                      isImage={isImage}
                                      isPdf={isPdf}
                                      isExcel={isExcel}
                                      colors={colors}
                                      onImageClick={isImage ? handleOpenLightbox : null}
                                    />
                                  );
                                })}
                              </Box>
                            </TableCell>
                          </TableRow>
                        </>
                      )}

                      <TableRow>
                        <TableCell colSpan={2} sx={{ p: 0 }}>
                          <Divider sx={{ my: 2, backgroundColor: "#34495e", height: 2 }} />
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  ))}
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>
    );
  };

  return (
    <Box sx={{ mb: 4 }}>
      {renderSection()}

      {/* Lightbox for image preview */}
      {lightboxOpen && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            bgcolor: "rgba(0, 0, 0, 0.9)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
            cursor: "pointer",
          }}
          onClick={handleCloseLightbox}
        >
          <Box sx={{ maxWidth: "90%", maxHeight: "90%" }}>
            <img
              src={lightboxImage}
              alt="Preview"
              style={{
                maxWidth: "100%",
                maxHeight: "90vh",
                objectFit: "contain",
              }}
            />
          </Box>
          <Button
            variant="contained"
            sx={{
              position: "absolute",
              top: 20,
              right: 20,
              bgcolor: "white",
              color: "black",
              "&:hover": { bgcolor: "grey.300" },
            }}
            onClick={handleCloseLightbox}
          >
            Close
          </Button>
        </Box>
      )}
    </Box>
  );
};

// Working Space Document Component – unchanged
const WorkingSpaceDocument = ({
  doc,
  index,
  isPdf,
  isExcel,
  colors,
  onImageClick,
}) => {
  const [imageUrl, setImageUrl] = React.useState("");
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    const loadDocument = async () => {
      try {
        setLoading(true);
        if (doc) {
          if (s3Utils.isJustFilename(doc)) {
            const signedUrl = await s3Utils.generateSignedUrlForFilename(doc);
            setImageUrl(signedUrl);
          } else {
            setImageUrl(doc);
          }
        }
      } catch (error) {
        console.error("Error loading document:", error);
        setImageUrl("");
      } finally {
        setLoading(false);
      }
    };

    loadDocument();
  }, [doc]);

  if (loading) {
    return (
      <Box
        sx={{
          width: 80,
          height: 80,
          border: `2px solid ${colors.grey[300]}`,
          borderRadius: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: colors.grey[100],
        }}
      >
        <Typography variant="caption" color="text.secondary">
          Loading...
        </Typography>
      </Box>
    );
  }

  if (isPdf || isExcel) {
    return (
      <Box
        sx={{
          width: 80,
          height: 80,
          border: `2px solid ${colors.grey[300]}`,
          borderRadius: 2,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
          transition: "all 0.2s",
          bgcolor: isPdf
            ? alpha(colors.error.main, 0.05)
            : alpha(colors.success.main, 0.05),
          "&:hover": {
            borderColor: isPdf ? colors.error.main : colors.success.main,
            transform: "scale(1.05)",
            boxShadow: 3,
          },
        }}
        onClick={() => window.open(imageUrl, "_blank")}
      >
        {isPdf ? (
          <PictureAsPdfIcon
            sx={{ fontSize: 30, color: colors.error.main, mb: 1 }}
          />
        ) : (
          <DescriptionIcon
            sx={{ fontSize: 30, color: colors.success.main, mb: 1 }}
          />
        )}
        <Typography
          variant="caption"
          sx={{ fontWeight: 600, textAlign: "center", px: 1 }}
        >
          {isPdf ? "PDF" : "Excel"}
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ fontSize: "0.40rem" }}
        >
          Click to view
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: 80,
        height: 80,
        border: `2px solid ${colors.grey[300]}`,
        borderRadius: 2,
        overflow: "hidden",
        cursor: "pointer",
        transition: "all 0.2s",
        "&:hover": {
          borderColor: colors.primary.main,
          transform: "scale(1.05)",
          boxShadow: 3,
        },
      }}
      onClick={() =>
        onImageClick ? onImageClick(imageUrl) : window.open(imageUrl, "_blank")
      }
    >
      <img
        src={imageUrl}
        alt={`Evidence ${index + 1}`}
        style={{
          width: "100%",
          height: "100%",
          objectFit: "cover",
        }}
        onError={(e) => {
          e.target.src =
            "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='120' height='120'%3E%3Crect fill='%23f0f0f0' width='120' height='120'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' fill='%23999' font-size='12'%3EImage%3C/text%3E%3C/svg%3E";
        }}
      />
    </Box>
  );
};

export default TestingWitnessedForm11Calibration;